import { useApolloClient, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import React, { useMemo } from "react";
import { Route, Switch, useParams, useRouteMatch } from "react-router";

import { CategoryAccess, Licensee, LicenseeType } from "../../../schema";

import { DocumentAddIcon } from "@heroicons/react/outline";
import cn from "classnames";
import { GraphQLError } from "graphql";
import { NavLink } from "react-router-dom";
import ReactDocumentTitle from "../../../components/ReactDocumentTitle";
import IconEmail from "../../../components/icon/IconEmail";
import IconLocalPin from "../../../components/icon/IconLocalPin";
import AppLayout from "../../../layouts/AppLayout";
import { addressToLines } from "../../../models/utils/Addresses";
import OldUrls from "../../../models/utils/OldUrls";
import Avatar from "../../../ui/Avatar";
import DropdownActionMenu from "../../../ui/DropdownActionMenu";
import DropdownMenuItem from "../../../ui/DropdownMenuItem";
import DropdownMenuSection from "../../../ui/DropdownMenuSection";
import H1 from "../../../ui/H1";
import TextButton from "../../../ui/TextButton";
import { showError, showInfo } from "../../../ui/ToastNotification";
import UIContainer from "../../../ui/UIContainer";
import { reverse } from "../../../urls";
import NotFoundScene from "../../system/NotFoundScene";
import UnexpectedErrorScene from "../../system/UnexpectedErrorScene";
import LicenseeProfileSkeleton from "./LicenseeProfileSkeleton";
import AccessStatus from "./components/AccessStatus";
import { EventTypeAccessTab } from "./components/EventTypeAccessTab";
import LicenseeTypeStatus from "./components/LicenseeTypeStatus";
import { LicensesTab } from "./components/LicensesTab";
import LimitAccessToggle from "./components/LimitAccessToggle";
import { ManagersTab } from "./components/ManagersTab";
import { ProfileTab } from "./components/ProfileTab";
import { SublicensesTab } from "./components/SublicensesTab";

interface QueryParams {
  id: string;
}

export default function LicenseeProfileScene() {
  const { id } = useParams<QueryParams>();
  const match = useRouteMatch();
  const client = useApolloClient();

  const referrer = useMemo(() => document.referrer, [id]);

  const { loading, data, error } = useQuery<LicenseeProfileQuery>(query, {
    variables: { id },
  });

  if (error) {
    return (
      <UnexpectedErrorScene error={error} errorInfo={null} eventId={null} />
    );
  }

  const goBack = () => window.location.assign(referrer);

  const onAccessLimit = (state: boolean) => {
    client
      .mutate({
        mutation: licenseeAccessMutation,
        variables: { id },
        optimisticResponse: {
          toggleLicenseeAccess: {
            id: data?.categoryAccess.id,
            __typename: "CategoryAccess",
            limited: state,
          },
        },
      })
      .then(
        () => {
          if (state) {
            showInfo("Access limit is imposed. The licensee is notified");
          } else {
            showInfo("Access limit is removed. The licensee is notified");
          }
        },
        (apolloError) => {
          apolloError.graphQLErrors.map((e: GraphQLError) =>
            showError(e.message)
          );
        }
      );
  };

  const addressLines = data?.licensee
    ? addressToLines(data?.licensee.address)
    : [];

  function isTrainer() {
    return data?.licensee.type === LicenseeType.Trainer;
  }

  const objectId =
    (isTrainer() ? data?.licensee.trainer?.id : data?.licensee.company?.id) ||
    "0";

  return (
    <>
      <ReactDocumentTitle title={"Licensee"} />
      <AppLayout className="" fullScreen={true}>
        <div className="absolute h-24 w-24"></div>
        {loading && <LicenseeProfileSkeleton />}
        {!loading && (!data || !data.licensee) && <NotFoundScene />}
        {!loading && !!data && data.licensee && (
          <>
            <div className="bg-gray-light">
              <UIContainer>
                <TextButton className="text-left pl-0 mt-4" onClick={goBack}>
                  &larr; Back
                </TextButton>
                <div className="flex mt-1 pb-10">
                  <Avatar
                    name={data.licensee.name}
                    className="rounded-md w-28 h-28"
                    url={data?.licensee.avatar || undefined}
                  />
                  <div className="flex flex-1 flex-col ml-4 justify-between">
                    <H1>{data.licensee.name}</H1>
                    <div className="flex">
                      <LicenseeTypeStatus
                        type={data.licensee.type}
                        className="mr-10"
                      />
                      <div className="text-blue mr-10">
                        <IconEmail className="mr-1" />
                        <a href={"mailto:" + data?.licensee.email}>
                          {data?.licensee.email}
                        </a>
                      </div>
                      <AccessStatus
                        limited={data.categoryAccess.limited}
                        className={"mr-10"}
                      />
                    </div>
                    <div className="max-w-md">
                      {addressLines.length > 0 && (
                        <>
                          <IconLocalPin className="text-gray-dark inline -mt-0.5 mr-1" />{" "}
                          {addressLines.join(", ")}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col ml-2">
                    <DropdownActionMenu
                      caption="Edit"
                      onButtonClick={() =>
                        window.location.assign(
                          isTrainer()
                            ? OldUrls.editTrainer(objectId)
                            : reverse("licensee_edit_company", { id })
                        )
                      }
                    >
                      <DropdownMenuSection title="Access">
                        <LimitAccessToggle
                          limited={data.categoryAccess.limited}
                          onChange={onAccessLimit}
                        />
                      </DropdownMenuSection>
                      <DropdownMenuSection title="License">
                        <DropdownMenuItem
                          caption="Add license"
                          onClick={() => {
                            window.location.href = OldUrls.addLicense(id || "");
                          }}
                        >
                          <DocumentAddIcon className="text-gray-dark w-5 h-5 inline-block mb-0.5 -ml-1 mr-3" />
                        </DropdownMenuItem>
                      </DropdownMenuSection>
                    </DropdownActionMenu>
                  </div>
                </div>
                <NavLink
                  exact
                  to={reverse("licensee_profile", { id: data.licensee.id })}
                  className={(isActive: boolean) =>
                    cn("font-bold mr-6 outline-none py-2 inline-block", {
                      "border-b-4 border-blue text-black": isActive,
                      "text-blue": !isActive,
                    })
                  }
                >
                  Profile
                </NavLink>
                <NavLink
                  exact
                  to={reverse("licensee_licenses", { id: data.licensee.id })}
                  className={(isActive: boolean) =>
                    cn("font-bold mr-6 outline-none py-2 inline-block", {
                      "border-b-4 border-blue text-black": isActive,
                      "text-blue": !isActive,
                    })
                  }
                >
                  Licenses
                </NavLink>
                <NavLink
                  exact
                  to={reverse("licensee_access", { id: data.licensee.id })}
                  className={(isActive: boolean) =>
                    cn("font-bold mr-6 outline-none py-2 inline-block", {
                      "border-b-4 border-blue text-black": isActive,
                      "text-blue": !isActive,
                    })
                  }
                >
                  Access to event types
                </NavLink>
                {!isTrainer() && (
                  <>
                    <NavLink
                      exact
                      to={reverse("licensee_sublicenses", {
                        id: data.licensee.id,
                      })}
                      className={(isActive: boolean) =>
                        cn("font-bold mr-6 outline-none py-2 inline-block", {
                          "border-b-4 border-blue text-black": isActive,
                          "text-blue": !isActive,
                        })
                      }
                    >
                      Trainers
                    </NavLink>
                    <NavLink
                      exact
                      to={reverse("licensee_managers", {
                        id: data.licensee.id,
                      })}
                      className={(isActive: boolean) =>
                        cn("font-bold mr-6 outline-none py-2 inline-block", {
                          "border-b-4 border-blue text-black": isActive,
                          "text-blue": !isActive,
                        })
                      }
                    >
                      Managers
                    </NavLink>
                  </>
                )}
              </UIContainer>
            </div>
            <UIContainer className="pb-10">
              <Switch>
                <Route exact path={`${match.path}`}>
                  <ProfileTab licensee={data.licensee} />
                </Route>
                <Route path={`${match.path}/licenses`}>
                  <LicensesTab licenseeId={data.licensee.id} />
                </Route>
                <Route path={`${match.path}/access`}>
                  <EventTypeAccessTab licenseeId={data.licensee.id} />
                </Route>
                <Route path={`${match.path}/sub-licenses`}>
                  <SublicensesTab licenseeId={data.licensee.id} />
                </Route>
                <Route path={`${match.path}/managers`}>
                  <ManagersTab licenseeId={data.licensee.id} />
                </Route>
              </Switch>
            </UIContainer>
          </>
        )}
      </AppLayout>
    </>
  );
}

export interface LicenseeProfileQuery {
  licensee: Licensee;
  categoryAccess: Pick<CategoryAccess, "id" | "limited" | "active">;
}

const query = gql`
  query LicenseeProfileQuery($id: ID!) {
    licensee: licensee(licenseeId: $id) {
      id
      name
      avatar
      type
      email
      address {
        street1
        street2
        city
        province
        postcode
        country
      }
      trainer {
        id
        bio
      }
      company {
        id
        description
      }
      billingAddresses {
        name
        street1
        street2
        city
        province
        postcode
        country
        taxNumber
      }
    }
    badges: licenseeBadges(licenseeId: $id) {
      id
      name
      image
    }
    categoryAccess: categoryAccess(licenseeId: $id) {
      id
      limited
      active
    }
  }
`;

const licenseeAccessMutation = gql`
  mutation LimitAccessStatus($id: ID!) {
    toggleLicenseeAccess(licenseeId: $id) {
      id
      limited
    }
  }
`;
